// Lifecycle helper for the PowerSync capture client — the coalesce-drain
// `flush()` used by the logout pre-flush (#726). Extracted from
// capture-client-powersync.ts to keep that file under the 500-line gate (the
// #726 work pushed it over). Pure function over the client's coalesce map; no
// module-level state.

import { canonicalizeQuantity } from '../../hooks/powersync/quantity-canonical'
import { _emitEnqueued } from '../../hooks/powersync/upload-events'
import {
  defaultAddBeforeUnloadListener,
  defaultAddPageHideListener,
  defaultAddVisibilityChangeListener,
  defaultGetVisibilityState,
} from './capture-client-defaults'
import type {
  CaptureClientDeps,
  CaptureClientWithInternals,
} from './capture-client'
import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'

/**
 * Canonicalise a committed capture_items quantity BEFORE the local SQLite write,
 * so a measurement keypad draft ("5." from the decimal tap, "" from Clear) is
 * never persisted locally or queued for sync — the ROOT of the
 * powersync_upload_jam wedge (the backend regex rejects "5." invalid_argument →
 * the connector rethrows → the device queue wedges). Falls back to the raw value
 * when it is not a repairable draft, leaving the backend regex as the
 * authoritative validator (never silently round a count). See quantity-canonical.ts.
 */
export function committedQuantity(raw: string): string {
  return canonicalizeQuantity(raw) ?? raw
}

/**
 * One open coalesce window: the latest pending `updateItem` for an itemId, the
 * dispatch promise resolvers waiting on its persist, and the debounce timer
 * that fires the flush. Owned here (alongside the flush/destroy helpers that
 * operate on the map) rather than in capture-client-powersync.ts so that file
 * stays under the 500-line gate; that module imports it back.
 */
export interface CoalesceEntry {
  itemId: string
  current: Extract<CaptureMutation, { kind: 'updateItem' }>
  resolvers: Array<{
    resolve: (r: CaptureMutationResult) => void
    reject: (e: unknown) => void
  }>
  debounceTimer: ReturnType<typeof setTimeout>
  /**
   * Consecutive failed-flush count for THIS held entry (F6 #811 / audit bh-1).
   * Undefined/0 for a normal tap; incremented each time `requeueFailedFlush`
   * re-arms a retry after a rejected persist, so the retry interval can back off
   * exponentially instead of busy-looping at `debounceMs` on a permanently
   * failing local write. Reset implicitly when the entry drains (success deletes
   * it) — a fresh tap starts a new entry at 0.
   */
  failedAttempts?: number
}

/** Ceiling for the failed-flush retry interval (F6 bh-1). A permanently failing
 *  local write backs off exponentially from the debounce base up to this cap —
 *  one retry per minute — instead of a ~2×/s busy-loop. Caps the INTERVAL only,
 *  never the retry COUNT (the count stays held so logout still fails closed). */
const MAX_FAILED_FLUSH_BACKOFF_MS = 60_000

/**
 * The coalesce-map shape `flushCoalesceAll` touches. Only `keys()` is needed —
 * the actual draining is delegated to the caller's `flushCoalesce`, which
 * removes the entry and clears its debounce timer. Kept structural (not the
 * full CoalesceEntry type) so this module has no coupling to the entry shape.
 */
type CoalesceKeys = { keys(): IterableIterator<string> }

export type InFlightCoalesceFlushes = Set<Promise<void>>

/** The coalesce-map shape `destroyCoalesce` touches: enough to clear each
 *  entry's debounce timer and empty the map. */
type CoalesceTimers = {
  values(): IterableIterator<{ debounceTimer: ReturnType<typeof setTimeout> }>
  clear(): void
}

/**
 * Drain EVERY open coalesce window to durable storage; resolve once each write
 * has landed in local SQLite / ps_crud. The logout flow awaits this (via the
 * capture-client registry) BEFORE the held-count guard reads the device,
 * closing #726: a +/- tap still inside the debounce window is persisted so the
 * guard surfaces it instead of the sign-out clear silently destroying it.
 *
 * `flushCoalesce` re-throws if its UPDATE fails, so a failed persist surfaces
 * here as a rejection and the logout preflight FAILS CLOSED to the loud warning
 * rather than clearing a count it could not persist. Uses `allSettled` (not
 * `Promise.all`) so EVERY entry's promise is observed — `Promise.all` would
 * leave the non-first rejections unhandled when several writes fail at once —
 * then re-throws the first rejection. Snapshot the keys first; flushCoalesce
 * mutates the map as it runs.
 *
 * Internal: `createCaptureLifecycle` exposes this as the client's `flush()`.
 */
async function flushCoalesceAll(
  coalesce: CoalesceKeys,
  flushCoalesce: (itemId: string) => Promise<void>,
  inFlight: InFlightCoalesceFlushes,
): Promise<void> {
  const alreadyInFlight = Array.from(inFlight)
  const results = await Promise.allSettled(
    Array.from(coalesce.keys())
      .map((itemId) => flushCoalesce(itemId))
      .concat(alreadyInFlight),
  )
  const failed = results.find((r) => r.status === 'rejected')
  if (failed?.status === 'rejected') throw failed.reason
}

export function trackCoalesceFlush(
  inFlight: InFlightCoalesceFlushes,
  promise: Promise<void>,
): Promise<void> {
  inFlight.add(promise)
  return promise.finally(() => {
    inFlight.delete(promise)
  })
}

/**
 * The coalesce-map shape `requeueFailedFlush` mutates: it must read the latest
 * entry for an itemId (a newer tap may have re-opened the window mid-flush) and
 * re-insert a durable retry entry. Structural (not the full map type) so this
 * module stays decoupled from the adapter internals, mirroring `CoalesceKeys`.
 */
export type CoalesceRetryMap = {
  get(itemId: string): CoalesceEntry | undefined
  set(itemId: string, entry: CoalesceEntry): void
}

/**
 * F6 (#811) — DO NOT silently drop a count whose coalesce flush rejected.
 *
 * `flushCoalesce` deletes the entry from the map BEFORE its `db.execute`, so a
 * rejected local write (SQLITE_BUSY / locked / disk-full) used to leave the
 * count nowhere: not on screen (the dispatch promise rejected → optimistic UI
 * reverted) and not in SQLite/ps_crud (the write failed). The debounce timer's
 * `.catch(() => {})` swallowed the rejection — no retry, permanent loss. This
 * re-arms a durable retry instead: it re-inserts the failed mutation into the
 * coalesce (so `pendingCount()` counts it again, the #807 tab-lifecycle flush
 * and the #726 logout pre-flush both retry it, and the count fails CLOSED) and
 * pulses the held-work signal so the unsynced-count indicator re-probes and the
 * surveyor sees the change is still pending rather than a stale "all saved".
 *
 * The original `resolvers` are NOT carried onto the retry entry — `flushCoalesce`
 * already rejected them so its caller's optimistic UI reverted (the #811 spec:
 * "the dispatch promise rejects"). The retry is a fresh durable attempt with no
 * awaiter; its own success/failure resolves/re-arms via the normal flush path.
 *
 * If a NEWER tap re-opened the window for this itemId while the failed flush was
 * in flight, that entry already supersedes the failed mutation (latest-tap-wins,
 * PRD §7.3.4) and IS already scheduled — so we leave it untouched and only pulse
 * the signal. Re-inserting the stale mutation over it would regress the count.
 *
 * RETRY INTERVAL (audit bh-1): the retry uses bounded EXPONENTIAL BACKOFF, not a
 * flat `debounceMs` re-arm. A permanently failing local write (corrupt row /
 * full disk) would otherwise busy-loop ~2×/s, re-touching SQLite and contending
 * the write lock with every other capture. Backoff caps the INTERVAL (settling
 * to one retry per `maxMs`); it NEVER caps the retry COUNT — a count cap would
 * re-introduce the silent drop this function exists to prevent. The count stays
 * HELD across every retry, so logout still fails closed on a never-recovering
 * device. `failedEntry.failedAttempts` carries the consecutive-failure count.
 */
export function requeueFailedFlush(
  coalesce: CoalesceRetryMap,
  failedEntry: CoalesceEntry,
  flushCoalesce: (itemId: string) => Promise<void>,
  baseMs: number,
): void {
  const failed = failedEntry.current
  const existing = coalesce.get(failed.itemId)
  if (existing === undefined) {
    const attempts = (failedEntry.failedAttempts ?? 0) + 1
    // baseMs (the debounce), doubling per consecutive failure, capped (bh-1).
    const delay = Math.min(
      baseMs * 2 ** (attempts - 1),
      MAX_FAILED_FLUSH_BACKOFF_MS,
    )
    coalesce.set(failed.itemId, {
      itemId: failed.itemId,
      current: failed,
      resolvers: [],
      failedAttempts: attempts,
      // Best-effort fire-and-forget retry: re-arm after the backoff so a
      // transient lock clears first, and swallow this timer's rejection the same
      // way the original debounce timer does — a still-failing write re-enters
      // HERE (attempts grows → interval backs off) and stays queued (durable),
      // never an unhandled rejection.
      debounceTimer: setTimeout(() => {
        void flushCoalesce(failed.itemId).catch(() => {})
      }, delay),
    })
  }
  // The held total rose back (the count is held, un-uploaded). The wrapped db's
  // enqueue pulse never fired because the write threw, so emit it here so the
  // indicator re-probes and the banner stops showing a stale "all saved".
  _emitEnqueued()
}

/**
 * Tear the coalesce + REST delegate down. Runs AFTER the best-effort
 * flush-on-unmount that `createCaptureLifecycle.destroy` kicks (#817), so by the
 * time it runs the coalesce map is already drained — the timer-clear loop +
 * `clear()` below are a defensive residual sweep (covering a flush that found an
 * empty window, or any future non-flushing caller, so nothing is left dangling),
 * then the REST delegate is destroyed.
 *
 * #817 — the "unmount/navigates" arm of #807: a tap still inside the 500 ms
 * coalesce when an in-app navigation unmounts the screen (an SPA route change
 * keeps the tab visible, so visibilitychange/pagehide do NOT fire) is NO LONGER
 * dropped. `destroy()` now kicks the same synchronous `db.execute` drain the
 * visibility/pagehide path uses, BEFORE this teardown runs, so the tap reaches
 * durable ps_crud. React cleanup still can't await, so the flush is best-effort
 * (kicked, not awaited) — the hard, awaited guarantee remains the logout
 * `flush()` (#726). Rohan's product call: the tap MUST survive.
 *
 * Internal: `createCaptureLifecycle` exposes this (plus the flush kick + listener
 * teardown) as the client's `destroy()`.
 */
function destroyCoalesce(
  coalesce: CoalesceTimers,
  rest: CaptureClientWithInternals,
): void {
  for (const entry of coalesce.values()) clearTimeout(entry.debounceTimer)
  coalesce.clear()
  rest.destroy()
}

/**
 * The tab-lifecycle listener seams the flush wires onto — a `Pick` of the four
 * lifecycle fields from `CaptureClientDeps` (audit-1 #6) so a rename of any seam
 * there breaks THIS type at compile time rather than silently leaving an
 * injected stub inert. All optional — each falls back to the real browser-global
 * default (capture-client-defaults.ts); tests pass stubs so jsdom quirks don't
 * leak into assertions.
 */
export type CaptureLifecycleListenerDeps = Pick<
  CaptureClientDeps,
  | 'getVisibilityState'
  | 'addVisibilityChangeListener'
  | 'addPageHideListener'
  | 'addBeforeUnloadListener'
>

/**
 * Install the tab-lifecycle coalesce flush (#807). `visibilitychange→hidden`
 * (phone lock / app switch / tab switch) and `pagehide` (tab close / bfcache —
 * the only signals a mobile OS reliably delivers before backgrounding a tab)
 * each drain the coalesce via `flush`, which *kicks* `db.execute` synchronously
 * (before its first await). This is BEST-EFFORT, not a hard durability
 * guarantee: PowerSync's `execute` commits on an async write-lock queue, so on a
 * terminal `pagehide` (hard tab-kill) the event loop can suspend before the
 * write lands — it is the best a JS / SQLite-WASM client can do without a
 * synchronous storage API. The dominant field case, `visibilitychange→hidden`
 * (lock / app-switch = freeze, not kill), gives the async write time to commit,
 * which is the loss this closes vs. the prior no-flush behaviour. A rejected
 * persist mid-freeze has no retry opportunity, so it is swallowed here rather
 * than raised as an unhandled rejection — the logout path (#726) still awaits
 * `flush()` directly and sees the rejection (fail-closed) on its own call.
 * `beforeunload` raises the native "Leave site?" guard while any write is
 * pending, reading the PowerSync client's OWN `pendingCount` (reading the REST
 * delegate's always-empty coalesce was the #807 mis-wiring). Returns a cleanup
 * that removes every listener.
 */
function installLifecycleFlush(
  flush: () => Promise<void>,
  pendingCount: () => number,
  deps: CaptureLifecycleListenerDeps,
): () => void {
  const getVisibilityState =
    deps.getVisibilityState ?? defaultGetVisibilityState
  const addVisibilityChangeListener =
    deps.addVisibilityChangeListener ?? defaultAddVisibilityChangeListener
  const addPageHideListener =
    deps.addPageHideListener ?? defaultAddPageHideListener
  const addBeforeUnloadListener =
    deps.addBeforeUnloadListener ?? defaultAddBeforeUnloadListener

  const drain = (): void => {
    void flush().catch(() => {})
  }
  const cleanups: Array<() => void> = [
    addVisibilityChangeListener(() => {
      if (getVisibilityState() === 'hidden') drain()
    }),
    addPageHideListener(() => {
      drain()
    }),
    addBeforeUnloadListener((event) => {
      if (pendingCount() === 0) return
      // Any truthy return triggers the browser's native "Leave site?" prompt;
      // set both preventDefault (modern spec) and returnValue (classic path).
      event.preventDefault()
      event.returnValue = ''
    }),
  ]
  return () => {
    for (const cleanup of cleanups) cleanup()
    cleanups.length = 0
  }
}

/** Inputs `createCaptureLifecycle` needs to own flush + destroy + the #807
 *  lifecycle listeners on behalf of the PowerSync capture client. */
export interface CaptureLifecycleConfig {
  coalesce: CoalesceKeys & CoalesceTimers
  flushCoalesce: (itemId: string) => Promise<void>
  inFlight: InFlightCoalesceFlushes
  rest: CaptureClientWithInternals
  pendingCount: () => number
  deps: CaptureLifecycleListenerDeps
}

/**
 * Build the PowerSync capture client's `flush()` / `destroy()` and install the
 * #807 tab-lifecycle flush in one place (keeping capture-client-powersync.ts
 * under the 500-line gate, like the per-dispatch helper extractions). `flush()`
 * drains the coalesce on demand (the #726 logout guarantee); `destroy()` removes
 * the lifecycle listeners, BEST-EFFORT FLUSHES the open coalesce window (#817 —
 * the same synchronous `db.execute` kick the visibility/pagehide drain uses, so
 * a +/- tap parked when an in-app navigation unmounts the screen still reaches
 * durable ps_crud), then tears the coalesce down. The flush is kicked, not
 * awaited by React cleanup; `destroy()` RETURNS the flush promise so the caller
 * (use-registered-capture-client) can retain it for the registry — that is what
 * lets the capture error-boundary Reload await this unmount flush even after the
 * screen unregisters (#810 P1). The hard guarantee stays the awaited logout
 * `flush()` (#726).
 */
export function createCaptureLifecycle(config: CaptureLifecycleConfig): {
  flush: () => Promise<void>
  destroy: () => Promise<void>
} {
  const { coalesce, flushCoalesce, inFlight, rest, pendingCount, deps } = config
  const flush = (): Promise<void> =>
    flushCoalesceAll(coalesce, flushCoalesce, inFlight)
  const removeLifecycleListeners = installLifecycleFlush(
    flush,
    pendingCount,
    deps,
  )
  const destroy = (): Promise<void> => {
    removeLifecycleListeners()
    // #817: best-effort flush-on-unmount. Kick the SAME synchronous `db.execute`
    // drain the visibility/pagehide path uses (flushCoalesce runs the UPDATE
    // before its first await) so a +/- tap still inside the 500 ms coalesce when
    // an SPA route change / React unmount tears this screen down reaches durable
    // ps_crud instead of being dropped (the "unmount/navigates" arm of #807).
    // MUST stay BEFORE destroyCoalesce (which empties the map) — pinned by
    // lint:powersync-capture-lifecycle-flush.
    const flushed = flush()
    // Safety net: swallow if NOBODY awaits the returned promise (a bare
    // destroy() in a test, or the REST path) so a rejected drain is never an
    // unhandled rejection. The RETURNED promise is still rejectable, so a caller
    // that retains it (#810 P1) — and the error-boundary / logout drain awaiting
    // it — observes the failure and fails closed.
    void flushed.catch(() => {})
    destroyCoalesce(coalesce, rest)
    return flushed
  }
  return { flush, destroy }
}
